/* screens/dev/UiKitParts1.jsx — витрина (часть 1): хелперы + фикстуры,
   типографика, палитра, иконки, кнопки, бейджи, чипы.
   Экспортирует вспомогательные Section/Demo и секции в window. */

import React from 'react';
import { CostStatusBadge, FoodCostBadge, KindBadge, Pill, RecipeBindingBadge, StatusBadge } from '../../components/ui/Badge.jsx';
import { Button } from '../../components/ui/Button.jsx';
import { AIButton, AddButton, BackLink, IconButton, Kebab } from '../../components/ui/Buttons.jsx';
import { CardTypeChip, Chip, ChipEditor, ChipList, FilterChip } from '../../components/ui/Chip.jsx';
import { Icon } from '../../components/ui/Icon.jsx';
import { PopoverItem } from '../../components/ui/Popover.jsx';
import { Spinner } from '../../components/ui/Spinner.jsx';
import { ToggleChips } from '../../components/ui/ToggleChips.jsx';
import { enums as E } from '../../lib/enums.js';

  const { useState } = React;

  /* ── общие хелперы витрины ── */
  function Section({ id, title, sub, children }) {
    return (
      <section className="uikit-section" id={id}>
        <h2 className="t-h3" style={{ marginBottom: 'var(--space-6)' }}>{title}</h2>
        {sub && <p className="uikit-sub t-body-sm">{sub}</p>}
        {children}
      </section>
    );
  }
  function StateBox({ label, children }) {
    return (
      <div className="uikit-panel">
        <div className="uikit-state-label">{label}</div>
        {children}
      </div>
    );
  }
  function Swatch({ name, varName }) {
    return (
      <div className="swatch">
        <div className="swatch__chip" style={{ background: `var(${varName})` }} />
        <div className="swatch__meta">
          <span className="swatch__name">{name}</span>
          <span className="swatch__val num">{varName}</span>
        </div>
      </div>
    );
  }

  /* ── Типографика ── */
  function TypographySection() {
    return (
      <Section id="type" title="Типографика" sub="Шрифтовая шкала «Линотип»: Geist (UI), Geist Mono (числа, tabular-nums), PT Serif (заголовки/обложки).">
        <div className="stack stack-2">
          <div className="type-row"><span className="t-display">PT Serif · 48px</span></div>
          <div className="type-row"><span className="t-h1">H1 · Заголовок раздела · 30px</span></div>
          <div className="type-row"><span className="t-h2">H2 · Подзаголовок · 24px</span></div>
          <div className="type-row"><span className="t-h3">H3 · Блок · 20px</span></div>
          <div className="type-row"><span className="t-h4">H4 · Подблок · 18px</span></div>
          <div className="type-row"><span className="t-body">Body · основной текст интерфейса · 15px</span></div>
          <div className="type-row"><span className="t-body-sm">Body small · 13px</span></div>
          <div className="type-row"><span className="t-caption">Caption · 12px подпись</span></div>
          <div className="type-row"><span className="t-eyebrow">Eyebrow · надзаголовок · 11px</span></div>
          <div className="type-row"><span className="num" style={{ fontSize: 20 }}>1 234,56 ₽ · 32,5 % · 250 г · Mono 11px</span></div>
        </div>
      </Section>
    );
  }

  /* ── Палитра ── */
  function PaletteSection() {
    return (
      <Section id="palette" title="Палитра — семантические токены" sub="Фон рабочей области — белый. Один акцент (индиго). Статусы: цвет + глиф/текст.">
        <div className="uikit-swatches">
          <Swatch name="Фон" varName="--bg" />
          <Swatch name="Поверхность" varName="--surface" />
          <Swatch name="Утопл. фон" varName="--bg-sunken" />
          <Swatch name="Граница" varName="--border" />
          <Swatch name="Текст" varName="--text" />
          <Swatch name="Текст dim" varName="--text-dim" />
          <Swatch name="Акцент" varName="--accent" />
          <Swatch name="Акцент soft" varName="--accent-soft" />
          <Swatch name="Успех" varName="--status-ok" />
          <Swatch name="Внимание" varName="--status-warn" />
          <Swatch name="Ошибка" varName="--status-bad" />
          <Swatch name="Инфо" varName="--status-info" />
        </div>
        {/* Серии графиков: цвет серии берётся по номеру, поэтому порядок первых шести
            неизменен; 7–10 — для касс, у которых категорий больше шести. */}
        <div className="uikit-state-label" style={{ marginTop: 'var(--space-12)' }}>
          Серии графиков — --c-series-1…10 (стек, донат, линии; одиннадцатая серия снова берёт первый цвет)
        </div>
        <div className="uikit-swatches">
          {Array.from({ length: 10 }, (_, i) => (
            <Swatch key={i} name={'Серия ' + (i + 1)} varName={'--c-series-' + (i + 1)} />
          ))}
        </div>
      </Section>
    );
  }

  /* ── Иконки ── */
  const ICON_NAMES = ['chat','kitchen','analytics','reports','restaurant','settings','search','filter','sort','plus','minus','edit','trash','copy','download','upload','refresh','eye','eye-off','link','unlink','check','check-circle','x-circle','alert-triangle','alert-circle','info','clock','calendar','camera','file','file-text','image','box','utensils','layers','tag','percent','scale','building','sparkles','grip','lock','external-link','more-vertical','chevron-down','chevrons-up-down','arrow-left','sun','moon'];
  function IconsSection() {
    return (
      <Section id="icons" title="Иконки — реестр Icon" sub="Линейные 24×24, stroke 1.5, currentColor. Никаких inline-SVG/эмодзи в разметке — только через <Icon/>.">
        <div className="uikit-icons">
          {ICON_NAMES.map((n) => (
            <div className="uikit-icon-cell" key={n}><Icon name={n} size={20} /><span>{n}</span></div>
          ))}
        </div>
      </Section>
    );
  }

  /* ── Кнопки ── */
  function ButtonsSection() {
    const [loading, setLoading] = useState(false);
    const [busy, setBusy] = useState(true);
    return (
      <Section id="buttons" title="Кнопки" sub="Единый Button (// заменяет PrimaryButton / GhostButton / LinkBtn) + производные: IconButton, AddButton, Kebab, AIButton, BackLink.">
        <div className="stack stack-12">
          <div className="uikit-grid">
            <Button variant="primary">Primary</Button>
            <Button variant="secondary">Secondary</Button>
            <Button variant="ghost">Ghost</Button>
            <Button variant="subtle">Subtle</Button>
            <Button variant="danger" iconLeft="trash">Danger</Button>
            <Button variant="primary" iconLeft="plus">С иконкой</Button>
            <Button variant="secondary" loading={loading} onClick={() => { setLoading(true); setTimeout(() => setLoading(false), 1600); }}>{loading ? 'Сохранение' : 'Кликни — loading'}</Button>
            <Button variant="primary" disabled>Disabled</Button>
          </div>
          <div className="uikit-grid">
            <Button variant="primary" size="sm">Small</Button>
            <Button variant="primary" size="md">Medium</Button>
            <Button variant="primary" size="lg">Large</Button>
            <IconButton icon="edit" tooltip="Редактировать" label="Редактировать" variant="secondary" />
            <IconButton icon="trash" tooltip="Удалить" label="Удалить" variant="ghost" />
            <Kebab>{({ close }) => (<div className="stack stack-2"><PopoverItem icon="edit" close={close}>Редактировать</PopoverItem><PopoverItem icon="copy" close={close}>Дублировать</PopoverItem><PopoverItem icon="trash" danger close={close}>Удалить</PopoverItem></div>)}</Kebab>
            <BackLink to="/dev/uikit">К реестру</BackLink>
          </div>
          <div className="uikit-grid">
            <AIButton busy={busy} onClick={() => setBusy((b) => !b)}>Спросить ассистента</AIButton>
            <AddButton>Добавить позицию</AddButton>
            <Spinner size="sm" /><Spinner size="md" /><Spinner size="lg" label="Загрузка" />
          </div>
          <div style={{ maxWidth: 360 }}><AddButton dashed>Добавить карточку в пустую область</AddButton></div>
        </div>
      </Section>
    );
  }

  /* ── Бейджи ── */
  function BadgesSection() {
    return (
      <Section id="badges" title="Бейджи и пилюли" sub="Статус всегда кодируется цветом + глифом/текстом (доступность). FC% — цветовая шкала <30 / 30–40 / >40.">
        <div className="stack stack-12">
          <div className="uikit-grid">
            {E.DEMO_STATUSES.map((s) => <StatusBadge key={s} status={s} />)}
          </div>
          <div className="uikit-grid">
            <span className="uikit-state-label" style={{ alignSelf: 'center' }}>Вид:</span>
            <KindBadge kind="dish" /><KindBadge kind="semi" /><KindBadge kind="ingredient" /><KindBadge kind="ttk" /><KindBadge kind="menu" />
          </div>
          <div className="uikit-grid">
            <span className="uikit-state-label" style={{ alignSelf: 'center' }}>Себест.:</span>
            {E.DEMO_COST_STATUSES.map((s) => <CostStatusBadge key={s} status={s} />)}
          </div>
          <div className="uikit-grid">
            <span className="uikit-state-label" style={{ alignSelf: 'center' }}>FC%:</span>
            {E.DEMO_FC_VALUES.map((v) => <FoodCostBadge key={v} value={v} />)}
          </div>
          <div className="uikit-grid">
            <span className="uikit-state-label" style={{ alignSelf: 'center' }}>Привязка:</span>
            <RecipeBindingBadge state="bound" /><RecipeBindingBadge state="unbound" /><RecipeBindingBadge state="add" onAdd={() => {}} /><RecipeBindingBadge state="add" readOnly />
            <span className="t-caption text-mute">(read-only «не привязан» — НЕ кнопка)</span>
          </div>
          <div className="uikit-grid">
            <Pill tone="accent">Business</Pill><Pill tone="ok">Активна</Pill><Pill tone="warn">Истекает</Pill><Pill tone="bad">Заблокирована</Pill><Pill tone="info">Beta</Pill><Pill tone="neutral" glyph={false}>Нейтрально</Pill>
          </div>
        </div>
      </Section>
    );
  }

  /* ── Чипы ── */
  const DAY_OPTIONS = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'].map((label, i) => ({ value: String(i), label }));
  const ROLE_OPTIONS = [
    { value: 'waiter', label: 'Официант', count: 14 },
    { value: 'bartender', label: 'Бармен', count: 4 },
    { value: 'manager', label: 'Менеджер', count: 3, title: 'Менеджеры зала и смены' },
  ];
  function ChipsSection() {
    const [tags, setTags] = useState(['Острое', 'Хит', 'Веган']);
    const [filters, setFilters] = useState({ dish: true, semi: false });
    const [ctype, setCtype] = useState('dish');
    const [staffGroup, setStaffGroup] = useState('waiter');
    const [days, setDays] = useState([]);
    const [roles, setRoles] = useState(['waiter']);
    const staffGroups = [
      { key: 'waiter', label: 'Официант', count: 14 },
      { key: 'manager', label: 'Менеджер', count: 3 },
      { key: 'banquet', label: 'Администраторы зала ночной смены и банкетов', count: 1 },
      { key: 'all', label: 'Все', count: null },
    ];
    return (
      <Section id="chips" title="Чипы" sub="Chip, ChipList (read-only +N), ChipEditor (Enter добавляет, × удаляет), FilterChip (фильтр с «×» и одиночный выбор dismissible={false}), CardTypeChip, ToggleChips (мультивыбор: «Все» = пустой выбор, снят последний — снова «Все»).">
        <div className="uikit-field-grid">
          <div className="uikit-demo-col"><div className="uikit-state-label">ToggleChips — дни недели («Производительность»)</div>
            <ToggleChips ariaLabel="Дни недели" options={DAY_OPTIONS} value={days} onChange={setDays} />
            <div className="t-caption text-mute">value: [{days.join(', ')}]{days.length ? '' : ' → «Все»'}</div></div>
          <div className="uikit-demo-col"><div className="uikit-state-label">ToggleChips — со счётчиком и подсказкой, свой allLabel</div>
            <ToggleChips ariaLabel="Должности" allLabel="Все должности" options={ROLE_OPTIONS} value={roles} onChange={setRoles} /></div>
          <div className="uikit-demo-col"><div className="uikit-state-label">Chip</div><div className="cluster cluster-4"><Chip tone="accent">Тег</Chip><Chip tone="ok" icon="check">Готово</Chip><Chip onRemove={() => {}}>Удаляемый</Chip></div></div>
          <div className="uikit-demo-col"><div className="uikit-state-label">ChipList (аллергены, max 3)</div><ChipList items={E.DEMO_ALLERGENS} max={3} tone="warn" /></div>
          <div className="uikit-demo-col"><div className="uikit-state-label">ChipEditor</div><ChipEditor value={tags} onChange={setTags} /></div>
          <div className="uikit-demo-col"><div className="uikit-state-label">FilterChip</div><div className="cluster cluster-6"><FilterChip active={filters.dish} count={42} onToggle={() => setFilters((f) => ({ ...f, dish: !f.dish }))}>Блюда</FilterChip><FilterChip active={filters.semi} count={18} onToggle={() => setFilters((f) => ({ ...f, semi: !f.semi }))}>Полуфабрикаты</FilterChip></div></div>
          <div className="uikit-demo-col"><div className="uikit-state-label">FilterChip dismissible={'{false}'} — одиночный выбор (группы «Персонала»)</div><div className="cluster cluster-6" role="group" aria-label="Группа сотрудников">{staffGroups.map((g) => (
            <FilterChip key={g.key} dismissible={false} title={g.label} active={staffGroup === g.key}
              count={g.count == null ? undefined : '· ' + g.count}
              onToggle={() => setStaffGroup(g.key)}>{g.label}</FilterChip>
          ))}</div></div>
          <div className="uikit-demo-col"><div className="uikit-state-label">CardTypeChip</div><CardTypeChip value={ctype} onChange={setCtype} /></div>
        </div>
      </Section>
    );
  }

export { Section as UiKitSection };
export { StateBox as UiKitStateBox };
export { Swatch as UiKitSwatch };
export { TypographySection };
export { PaletteSection };
export { IconsSection };
export { ButtonsSection };
export { BadgesSection };
export { ChipsSection };
